import { useCallback, useRef, ReactNode, useState } from "react";
import { Modal, ModalProps, Button, Buttons, ButtonAppearance } from "../../general";
import { Shelf } from "../../layout";
import { FormProps, Form } from "../Form";
import { SubmitButton } from "../SubmitButton";
import { FormModalCloseEventHandler } from "./FormModalCloseEventHandler";
import styles from "./styles.module.scss";
import { FieldValues, UseFormReturn } from "react-hook-form";

interface FormModalProps<TValues extends FieldValues>
  extends FormProps<TValues>,
    Omit<ModalProps, "size" | "onClose" | "footer" | "children"> {
  /**
   * The content within the submit button. If not specified defaults to "Submit".
   */
  readonly submitContent?: ReactNode;

  /**
   * The content within the cancel button. If not specified defaults to "Cancel".
   */
  readonly cancelContent?: ReactNode;

  /**
   * The content within the reset button. If not specified defaults to "Reset".
   */
  readonly resetContent?: ReactNode;

  /**
   * Called when the close button or cancel button are clicked, or submitting is complete.
   */
  readonly onClose: FormModalCloseEventHandler;

  /**
   * Indicates whether the form is able to be reset. If not specified defaults to `true`.
   */
  readonly canReset?: boolean;

  /**
   * The button appearance type for the primary "submit" button. Defaults to "Functional"
   */
  readonly submitButtonAppearance?: ButtonAppearance;
}

/**
 * A component which displays a Form within a Modal dialog.
 */
export const FormModal = <TValues extends FieldValues = FieldValues>(props: FormModalProps<TValues>) => {
  const {
    className,
    style,
    testId,
    visible,
    title,
    children,
    submitContent = "Submit",
    cancelContent = "Cancel",
    resetContent = "Reset",
    submitButtonAppearance = Buttons.Functional.Primary.Large,
    onSubmit,
    onClose,
    canReset = true,
    methodsRef,
    ...formProps
  } = props;
  const internalRef = useRef<UseFormReturn<TValues> | undefined | null>(formProps.form || undefined);
  const [submitting, setSubmitting] = useState(false);

  const submitHandler = useCallback(
    async (values: TValues) => {
      setSubmitting(true);

      if (!onSubmit) {
        setSubmitting(false);
        onClose(true);
        return;
      }

      try {
        await onSubmit(values);
        onClose(true);
      } finally {
        setSubmitting(false);
      }
    },
    [onSubmit, onClose],
  );

  const resetHandler = useCallback(() => {
    if (!canReset) return;
    if (!internalRef.current) return;

    internalRef.current.reset();
  }, [canReset]);

  const cancelHandler = useCallback(() => {
    if (submitting) return;

    onClose(false);
  }, [submitting, onClose]);

  const internalMethodsRef = useCallback(
    (ref: UseFormReturn<TValues> | null) => {
      internalRef.current = ref;

      if (!methodsRef) return;
      if (typeof methodsRef === "function") {
        methodsRef(ref);
      } else {
        methodsRef.current = ref;
      }
    },
    [methodsRef],
  );

  return (
    <Modal
      className={className}
      style={style}
      testId={testId}
      visible={visible}
      title={title}
      size="wide"
      onClose={cancelHandler}
      root={(children) => (
        <Form className={styles.ModalForm} {...formProps} methodsRef={methodsRef ? internalMethodsRef : undefined} onSubmit={submitHandler}>
          {children}
        </Form>
      )}
      footer={
        <Shelf spacing="small">
          {canReset && (
            <Button appearance={Buttons.Functional.Tertiary.Large} onClick={resetHandler} disabled={submitting}>
              {resetContent}
            </Button>
          )}
          <Shelf.Spacer />
          <Button appearance={Buttons.Functional.Secondary.Large} onClick={cancelHandler} disabled={submitting}>
            {cancelContent}
          </Button>
          <SubmitButton appearance={submitButtonAppearance} autoFocus>
            {submitContent}
          </SubmitButton>
        </Shelf>
      }
    >
      <>{children as ReactNode}</>
    </Modal>
  );
};
